import ProposalField, { ProposalFieldName, type ProposalFieldNameValue } from "work/proposals/proposal-fields/proposal-field";

/**
 * Converts a user-facing proposal field/thread into the canonical semantic path
 * used by negotiation events. The path is field-level: a collection item shares
 * its collection's thread, and is told apart within it by its item id rather
 * than by a path of its own — see {@link proposalTimelineItemPathForField} for
 * the one place an item does get its own key.
 */
export function proposalTimelinePathForField(field?: ProposalField | null): string | undefined {
  if (!field || field.name === ProposalFieldName.General) return undefined;

  return proposalTimelinePathForFieldName(field.name);
}

/**
 * The path a field's history control asks the timeline for. A structured scalar that carries an id
 * reads its own path (the team leader's is `/vendorTeam/leader`); every other field reads its field
 * path. `scalarPath` is `ProposalFieldTimelineDropdown`'s explicit override.
 *
 * Undefined for a field with no path, such as General. A control must then ask for nothing: the
 * endpoint reads a missing path as "the whole negotiation", so asking anyway drew every change on the
 * proposal into one field's control (#5648).
 */
export function proposalTimelineQueryPathForField(
  field?: ProposalField | null,
  scalarPath?: string,
): string | undefined {
  if (scalarPath) return scalarPath;
  if (!field) return undefined;
  const identity = field.timelineIdentity();
  return identity.kind === "scalarWithId" ? identity.path : proposalTimelinePathForField(field);
}

/**
 * The seen-cursor key for one item of a collection — `EVENT_NEGOTIATION_DESIGN.md`'s item thread,
 * `/feeSchedule/{categoryId}`.
 *
 * Only a field that keeps its own history per item has one, which today is a fee-schedule category:
 * every other collection counts and acknowledges at its collection's path, so its rows deliberately
 * share one cursor (#4951, #4954). Undefined for anything else, which is what keeps those collections
 * on the field-level path they were designed around.
 *
 * This is a cursor key, not a query path. The timeline is still fetched at the collection's own path
 * and narrowed by item id, because that is the shape the event stream and the server's thread filter
 * already speak — nothing writes events to this path.
 */
export function proposalTimelineItemPathForField(field?: ProposalField | null): string | undefined {
  if (!field?.isFeeScheduleCategory || !field.id) return undefined;
  const fieldPath = proposalTimelinePathForField(field);
  // Lower case, as the server writes a Guid: a category reached from an unread change carries the
  // stream's item id, one reached from an issue the projection's, and the two have not always agreed
  // on casing. Keyed as given, the landing wrote one cursor and the traverser read the other (#6617).
  return fieldPath && `${fieldPath}/${field.id.value.toLowerCase()}`;
}

/**
 * Reverse of {@link proposalTimelinePathForField}: maps a semantic negotiation path back to the
 * user-facing {@link ProposalField} it belongs to, so a change or a pending operation can be counted
 * against, and traversed to, the field it is about. Descendant and item paths resolve by their
 * leading segment (`/vendorTeam/members/…` → Team, `/discount/percent` → Discount). Returns undefined
 * for paths with no navigable field, letting callers degrade gracefully instead of throwing.
 */
export function proposalFieldForTimelinePath(path?: string | null): ProposalField | undefined {
  if (!path) return undefined;
  const segment = path.replace(/^\/+/, "").split("/")[0];
  switch (segment) {
    case "name":
      return ProposalField.Name;
    case "description":
      return ProposalField.Description;
    case "responseDueBy":
      return ProposalField.ResponseDueBy;
    case "startDate":
      return ProposalField.StartDate;
    case "endDate":
      return ProposalField.EndDate;
    case "clientTeam":
    case "vendorTeam":
    case "clientTeamTemplateIds":
    case "vendorTeamTemplateIds":
      return ProposalField.Team;
    case "teamRestricted":
      return ProposalField.TeamRestriction;
    case "feeSchedule":
    case "clientFeeScheduleTemplateIds":
    case "vendorFeeScheduleTemplateIds":
      return ProposalField.FeeSchedule;
    case "clientPolicies":
      return ProposalField.ClientPolicies;
    case "vendorPolicies":
      return ProposalField.VendorPolicies;
    case "conflicts":
      return ProposalField.Conflicts;
    case "conflictsCheckWaived":
      return ProposalField.WaiveConflictsCheck;
    case "discount":
      return ProposalField.Discount;
    default:
      return undefined;
  }
}

export function proposalTimelinePathForFieldName(fieldName: ProposalFieldNameValue): string | undefined {
  switch (fieldName) {
    case ProposalFieldName.Name:
      return "/name";
    case ProposalFieldName.Description:
      return "/description";
    case ProposalFieldName.ResponseDueBy:
      return "/responseDueBy";
    case ProposalFieldName.StartDate:
      return "/startDate";
    case ProposalFieldName.EndDate:
      return "/endDate";
    case ProposalFieldName.Team:
    case ProposalFieldName.TeamLeader:
      return "/vendorTeam";
    case ProposalFieldName.TeamRestriction:
      return "/teamRestricted";
    case ProposalFieldName.FeeSchedule:
      return "/feeSchedule";
    case ProposalFieldName.ClientPolicies:
      return "/clientPolicies";
    case ProposalFieldName.VendorPolicies:
      return "/vendorPolicies";
    case ProposalFieldName.Conflicts:
      return "/conflicts";
    case ProposalFieldName.WaiveConflictsCheck:
      return "/conflictsCheckWaived";
    case ProposalFieldName.Discount:
      return "/discount";
    default:
      return undefined;
  }
}
